/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { ReactNode } from"react";
import { Link } from'react-router';
import { cn } from"@/shared/ui/utils/tailwind";

export type SideMenuLinkProps = {
  label?: string;
  icon?: ReactNode;
  linkTo: string;
  badge?: number;
  isActive?: boolean;
  action?: () => void;
};

/**
 * Side menu link component with Tailwind styling
 */
export const SideMenuLink: React.FC<SideMenuLinkProps> = ({
  label,
  icon,
  linkTo,
  badge,
  isActive = false,
  action,
}) => {
  // Handle relative paths
  const fullPath = linkTo.startsWith("/") ? linkTo :`/${linkTo}`;

  return (
    <Link
      to={fullPath}
      className={cn(
        "flex flex-col items-center justify-center gap-[var(--space-1)]",
        "p-[var(--space-4)] px-[var(--space-2)] w-full min-h-[88px]",
        "rounded-none bg-transparent cursor-pointer transition-all duration-150",
        "no-underline relative text-inherit box-border border-none outline-none",
        "md:w-auto md:min-h-auto md:p-[var(--space-2)] md:gap-[var(--space-1)]",
        "hover:bg-[var(--surface-hover)]",
        isActive && [
          "active",
          "bg-[var(--selection-selected)]",
          "[&_.menuIcon]:text-[var(--seed-base)]",
          "[&_.menuLabel]:text-[var(--seed-base)] [&_.menuLabel]:font-semibold",
          "",
          "before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0",
          "before:w-[3px] before:bg-[var(--seed-base)] before:rounded-none before:z-[1]",
          "",
          "md:before:left-0 md:before:right-0 md:before:top-auto md:before:bottom-0",
          "md:before:w-full md:before:h-[3px]",
        ]
      )}
      onClick={action}
    >
      {icon && (
        <span className="menuIcon w-7 h-7 text-[var(--text-secondary)] transition-all duration-150 flex-shrink-0 flex items-center justify-center relative">
          {icon}
          {badge !== undefined && badge > 0 && (
            <span
              className={cn(
                "absolute -top-1 -right-1 w-2 h-2 bg-[var(--seed-base)]",
                "border-2 border-[var(--surface-primary)] rounded-full z-[1]",
                badge > 0 && [
                  "w-auto h-4 min-w-4 px-1 text-xs font-bold",
                  "text-[var(--text-inverse)] flex items-center justify-center",
                  "-top-2 -right-2",
                ]
              )}
            >
              {badge > 99 ?"99+" : badge}
            </span>
          )}
        </span>
      )}
      {label && (
        <span className="menuLabel text-xs text-[var(--text-secondary)] text-center leading-[1.4] transition-colors duration-150 whitespace-nowrap tracking-[0.02em] md:text-xs">
          {label}
        </span>
      )}
    </Link>
  );
};

export default SideMenuLink;
